// SYNC: duplicated verbatim across cres-t1/t4/t5/t6 (separate frontend apps, no shared package) —
// keep the copies in sync. The assistant BACKEND is centralized in cres-aws-t2; only this UI is copied.
// ParkourAI Assistant — a chat drawer for the NavBar. The assistant backend is centralized in the
// Developer (t2) app and reached cross-app (see data/assistant.ts); this app only renders the chat.
// This is the QMS panel (data/assistant.ts names the app): read-only over the quality system --
// records, who can see them, approvals, change orders, training, projects, quality records, the
// quote-to-build chain and attached files -- plus the Developer exception tools, the product's own
// documentation, and (Phase 2) drafts of changes that become proposal cards. Every answer is read
// under the persona the user is acting as; the greeting says so because it is the first thing to
// check when a record "does not exist".
//
// A DRAWER, NOT A POPOVER (Ed, 2026-10-28). The NavBar button toggles a right-hand drawer open and
// shut; the page underneath stays usable while it is open, so a user can ask about a record and
// then go and look at it without losing the thread. The conversation lives in this component,
// which the NavBar keeps mounted for the whole session, AND in sessionStorage, so toggling the
// drawer, moving between pages and even reloading the tab all keep the history. Signing out ends
// the tab's session and with it the stored thread; a different tab is a different conversation.
// Per-tool "activity" steps come back on the HTTP response and render under the reply. (No MQTT
// live-ticks here: the t2 backend pushes to its own broker/topic, not this app's.)
//
// NOTHING ABOUT THE MODEL IS SHOWN (Ed, 2026-10-29: "Do not show anything about the model"). The
// turn still reports which model answered, for the server's logs and the harness; the drawer
// does not render it.
//
// LINKS TO WHAT THE ANSWER REFERS TO (Ed, 2026-10-29). The server collects every record the tools
// returned this turn -- ObjectID and the revision USID the product itself links on -- and the
// drawer does two things with that list: an ObjectID in the reply text that matches a reference
// becomes a link, and the references are listed as chips under the reply. The model writes no
// links and no identifiers are trusted from its prose: a mention that matches nothing the tools
// showed stays plain text. Navigation is in-app, so the drawer stays open beside the record.
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router";
import {
    IconButton, Drawer, Box, Typography, Tooltip, TextField, CircularProgress, Button, Chip,
} from "@mui/material";
import {AutoAwesomeOutlined, SendRounded, CloseRounded, DeleteOutlineRounded, MenuBookOutlined, CheckRounded, OpenInNewRounded} from "@mui/icons-material";
import {sendAssistantMessage, AssistantReference, AssistantProposal, openGuide, ASK_ASSISTANT_EVENT, getAssistantModels, setAssistantModel, perTurnLabel, AssistantModels} from "../../data/assistant";
import {approveProposal, rejectProposal, targetLink, ProposalDecision} from "../../data/proposals";
import {ResizeGripper, useResizablePanel} from "../Common/ResizablePanel";

const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const TEXT = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E4E6E8";
const BLUE = "#1498D8";
const GREEN = "#1E7C4A";
const NAVBAR_HEIGHT = 50;      // NavBar.tsx draws a 50px bar; the drawer hangs below it so the toggle stays reachable
// THE WIDTH IS THE USER'S (Ed, 2026-10-29: "put a gripper that will allow the user to expand/contract
// the drawer" ... "Make the gripper look like the gripper on the left side"). The same hook and the same
// handle as the record rail and the Filter By sidebar (Common/ResizablePanel), on the drawer's left
// edge: a hairline with a small tab riding on it, the drag measured from the drawer's anchored right
// edge, the width kept in sessionStorage like theirs. A drawer never collapses from a drag -- the
// NavBar button is what hides it -- so the collapse threshold is zero and a click on the tab does nothing.
const DRAWER_DEFAULT_WIDTH = 440;
const DRAWER_MIN_WIDTH = 320;
const DRAWER_MAX_WIDTH = 1200;
const STORAGE_KEY = "AssistantChat:thread";

// A WRITE IS A CARD (Phase 2, 2026-10-29). When the assistant drafts a change, the turn carries a
// pending proposal and the drawer renders it as a card with Approve and Reject. Nothing is written
// until Approve; approval replays the drafted call under the user's persona and the card then
// offers to open what was made. The decision is kept in the stored thread, so a reload shows a
// decided card as decided rather than offering the same approval twice.
type ChatMessage =
    | {role: "user" | "assistant" | "error"; text: string; steps?: string[]; references?: AssistantReference[]}
    | {role: "proposal"; text?: undefined; proposal: AssistantProposal; deciding?: boolean; decision?: ProposalDecision; error?: string};

/** The reply text with every ObjectID that matches a reference turned into a link. Only the
 *  identifiers the server listed are matched, so the model cannot mint a link. */
function linkedText(text: string, references: AssistantReference[] | undefined, go: (path: string) => void): React.ReactNode {
    if (!references || references.length === 0) return text;
    const byId = new Map(references.filter((r) => r.kind !== "Documentation").map((r) => [r.objectId, r]));
    if (byId.size === 0) return text;
    const ids = Array.from(byId.keys()).sort((a, b) => b.length - a.length).map((id) => id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const pattern = new RegExp(`(${ids.join("|")})`, "g");
    return text.split(pattern).map((part, i) => {
        const ref = byId.get(part);
        if (!ref) return <React.Fragment key={i}>{part}</React.Fragment>;
        return (
            <Box component="span" key={i} role="link" tabIndex={0} title={ref.name || ref.objectId}
                 onClick={() => openReference(ref, go)} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter") openReference(ref, go); }}
                 sx={{color: BLUE, fontWeight: 600, cursor: "pointer", textDecoration: "underline", textDecorationColor: "#9CD0EC"}}>
                {part}
            </Box>
        );
    });
}

type StoredThread = {messages: ChatMessage[]; rawMessages: any[]};

const GREETING: ChatMessage = {
    role: "assistant",
    text: "Hi — I'm the ParkourAI Assistant. Ask me about your quality system: find a record and read what it says, who can see it and whether it can be edited; what is waiting on your signature and whether a record was approved; change orders, training, projects and quality records; or a question answered from the files attached to a record. I read only what your current persona can see. I can also draft a change for you — a new record, a revision, a submission for signature, a change order, a project — and nothing is written until you approve it here. I never sign.",
};

function loadThread(): StoredThread {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed?.messages) && parsed.messages.length > 0 && Array.isArray(parsed?.rawMessages)) {
                return {messages: parsed.messages, rawMessages: parsed.rawMessages};
            }
        }
    } catch {
        // A private window or a cleared store: start a fresh thread.
    }
    return {messages: [GREETING], rawMessages: []};
}

function saveThread(thread: StoredThread) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(thread));
    } catch {
        // Storage refused (quota, private mode): the in-memory thread still carries the session.
    }
}

/** Open a reference: a record or change order in this app; a documentation section in the guide
 *  window the Documentation page uses, so one guide reuses one window. */
function openReference(ref: AssistantReference, navigate: (path: string) => void) {
    if (ref.kind === "Documentation") {
        openGuide(ref.path);
        return;
    }
    navigate(ref.path);
}

export default function AssistantChat() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const {width, panelRef, onGripperDown} = useResizablePanel({
        storageKey: "AssistantChat", defaultWidth: DRAWER_DEFAULT_WIDTH, minWidth: DRAWER_MIN_WIDTH,
        maxWidth: DRAWER_MAX_WIDTH, collapseAt: 0, side: "right",
    });
    const [thread, setThread] = useState<StoredThread>(loadThread);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const {messages, rawMessages} = thread;

    useEffect(() => {
        saveThread(thread);
    }, [thread]);

    const listRef = useRef<HTMLDivElement | null>(null);
    const scrollToBottom = useCallback(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, []);
    useEffect(() => {
        if (open) scrollToBottom();
    }, [open, messages, loading, scrollToBottom]);

    const send = useCallback(async (seeded?: string) => {
        const text = (seeded ?? input).trim();
        if (!text || loading) return;
        setInput("");
        setThread((prev) => ({...prev, messages: [...prev.messages, {role: "user", text}]}));
        setLoading(true);
        try {
            const turn = await sendAssistantMessage(text, rawMessages);
            setThread((prev) => ({
                rawMessages: turn.messages,
                messages: [
                    ...prev.messages,
                    {
                        role: "assistant",
                        text: turn.reply || "(no response)",
                        steps: turn.activity.map((a) => a.text).filter(Boolean),
                        references: turn.references,
                    },
                    ...turn.proposals.map((p): ChatMessage => ({role: "proposal", proposal: p})),
                ],
            }));
        } catch {
            setThread((prev) => ({
                ...prev,
                messages: [...prev.messages, {role: "error", text: "Sorry — something went wrong reaching the assistant. Please try again."}],
            }));
        } finally {
            setLoading(false);
        }
    }, [input, loading, rawMessages]);

    // A SCREEN CAN HAND THE CONVERSATION OVER. Ed, 2026-10-29: "Provide a link to take the
    // conversation into AI Assist and seed the conversation so it has context." A screen raises
    // askAssistant(message) with what it knows -- the record, the field, what it found -- and the
    // drawer opens and sends that as the user's turn, so the answer arrives with the context and
    // the user never retypes what the screen already knew.
    useEffect(() => {
        const onAsk = (event: Event) => {
            const message = String((event as CustomEvent).detail?.message || "").trim();
            if (!message) return;
            setOpen(true);
            void send(message);
        };
        window.addEventListener(ASK_ASSISTANT_EVENT, onAsk);
        return () => window.removeEventListener(ASK_ASSISTANT_EVENT, onAsk);
    }, [send]);

    const decide = useCallback(async (usid: string, action: "approve" | "reject") => {
        const mark = (patch: Partial<Extract<ChatMessage, {role: "proposal"}>>) =>
            setThread((prev) => ({...prev, messages: prev.messages.map((m) => (m.role === "proposal" && m.proposal.usid === usid ? {...m, ...patch} : m))}));
        mark({deciding: true, error: undefined});
        try {
            const res = action === "approve" ? await approveProposal(usid) : await rejectProposal(usid);
            mark({deciding: false, decision: res});
        } catch (problem: any) {
            const refusal = problem?.response?.data;
            const why = refusal?.Message || refusal?.message || (refusal?.Code ? `Refused: ${refusal.Code}` : null);
            mark({deciding: false, error: why || "Couldn't complete that — please try again."});
        }
    }, []);

    const clearThread = () => {
        if (loading) return;
        setThread({messages: [GREETING], rawMessages: []});
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    return (
        <>
            <Tooltip title={open ? "Hide the ParkourAI Assistant" : "ParkourAI Assistant"}>
                <IconButton onClick={() => setOpen((o) => !o)} size="small" aria-label="assistant" aria-pressed={open}>
                    <AutoAwesomeOutlined sx={{color: BLUE}} />
                </IconButton>
            </Tooltip>
            {/* persistent: no backdrop and no click-away, so the page stays usable and the NavBar
                button is the one thing that opens and closes it (plus the X in the header). */}
            <Drawer
                anchor="right"
                variant="persistent"
                open={open}
                slotProps={{paper: {ref: panelRef, sx: {
                    width: `min(${width}px, 92vw)`, top: NAVBAR_HEIGHT, height: `calc(100% - ${NAVBAR_HEIGHT}px)`,
                    display: "flex", flexDirection: "column", fontFamily: FONT, overflow: "visible",
                    borderLeft: "none", boxShadow: "-4px 0 16px rgba(0,0,0,0.08)",
                }}}}
            >
                {/* The gripper: the same hairline-and-tab as the record rail, on the drawer's left
                    edge. The wrapper is a flex column the full height of the paper so the 1px strip
                    stretches and the tab centres where a hand goes looking for it; the paper keeps
                    overflow visible so the tab, which overhangs the page, can be seen and pressed. */}
                <Box sx={{position: "absolute", left: 0, top: 0, bottom: 0, display: "flex", alignItems: "stretch", zIndex: 1}}>
                    <ResizeGripper collapsed={false} onMouseDown={onGripperDown} setCollapsed={() => undefined}
                                   openLabel="Drag to resize" side="right" />
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`}}>
                    <AutoAwesomeOutlined sx={{color: BLUE, fontSize: 20}} />
                    <Typography sx={{fontSize: 15, fontWeight: 700, color: TEXT, fontFamily: FONT, flex: 1}}>ParkourAI Assistant</Typography>
                    <ModelPicker open={open}/>
                    <Tooltip title="Start a new conversation">
                        <span>
                            <IconButton onClick={clearThread} size="small" aria-label="new conversation" disabled={loading || messages.length <= 1}>
                                <DeleteOutlineRounded sx={{fontSize: 20, color: MUTED}} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Hide">
                        <IconButton onClick={() => setOpen(false)} size="small" aria-label="hide assistant">
                            <CloseRounded sx={{fontSize: 20, color: MUTED}} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box ref={listRef} sx={{flex: 1, overflowY: "auto", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 1.25}}>
                    {messages.map((m, i) => m.role === "proposal" ? (
                        <ProposalCard key={i} msg={m} onDecide={decide} onOpen={(path) => navigate(path)} />
                    ) : (
                        <Box
                            key={i}
                            sx={{
                                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                                maxWidth: "88%",
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                fontFamily: FONT,
                                fontSize: 13.5,
                                lineHeight: 1.5,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                color: m.role === "error" ? "#B3261E" : TEXT,
                                backgroundColor: m.role === "user" ? "#E9F4FB" : m.role === "error" ? "#FCECEA" : "#F5F6F7",
                                border: m.role === "user" ? `1px solid ${BORDER}` : "none",
                            }}
                        >
                            {m.role === "assistant" && m.steps && m.steps.length > 0 && (
                                <Box sx={{mb: 0.75}}>
                                    {m.steps.map((s, j) => (
                                        <Typography key={j} sx={{fontSize: 11.5, color: MUTED, fontFamily: FONT}}>
                                            ✓ {s}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                            {m.role === "assistant" ? linkedText(m.text, m.references, navigate) : m.text}
                            {m.role === "assistant" && m.references && m.references.length > 0 && (
                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1}}>
                                    {m.references.map((ref) => (
                                        <Box component="button" type="button" key={ref.objectId} onClick={() => openReference(ref, navigate)}
                                             title={ref.kind === "Documentation" ? "Open this section of the documentation" : `Open ${ref.objectId}`}
                                             sx={{fontFamily: FONT, fontSize: 11.5, lineHeight: 1.4, color: BLUE, backgroundColor: "#FFFFFF",
                                                  border: `1px solid ${BORDER}`, borderRadius: "12px", px: 1, py: 0.25, cursor: "pointer",
                                                  maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                  "&:hover": {borderColor: BLUE, backgroundColor: "#E9F4FB"}}}>
                                            {ref.kind === "Documentation"
                                                ? <><MenuBookOutlined sx={{fontSize: 13, verticalAlign: "-2px", mr: 0.5}} />{ref.name || ref.objectId}</>
                                                : <><strong>{ref.objectId}</strong>{ref.name ? ` · ${ref.name}` : ""}</>}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    ))}

                    {loading && (
                        <Box sx={{alignSelf: "flex-start", maxWidth: "88%", px: 1.5, py: 1}}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                <CircularProgress size={13} sx={{color: BLUE}} />
                                <Typography sx={{fontSize: 12.5, color: MUTED, fontFamily: FONT}}>Thinking…</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                <Box sx={{borderTop: `1px solid ${BORDER}`, px: 1.5, py: 1.25}}>
                    <Box sx={{display: "flex", alignItems: "flex-end", gap: 1}}>
                        <TextField
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Ask about your quality system…"
                            multiline
                            maxRows={4}
                            fullWidth
                            size="small"
                            sx={{"& .MuiInputBase-input": {fontFamily: FONT, fontSize: 13.5}}}
                        />
                        <IconButton onClick={() => send()} disabled={!input.trim() || loading} aria-label="send" sx={{color: BLUE}}>
                            <SendRounded />
                        </IconButton>
                    </Box>
                    <Typography sx={{fontSize: 10.5, color: MUTED, fontFamily: FONT, mt: 0.5, textAlign: "center"}}>
                        I read what your persona can see. A change I draft is written only when you approve it.
                    </Typography>
                </Box>
            </Drawer>
        </>
    );
}


function ProposalCard({msg, onDecide, onOpen}: {
    msg: Extract<ChatMessage, {role: "proposal"}>;
    onDecide: (usid: string, action: "approve" | "reject") => void;
    onOpen: (path: string) => void;
}) {
    const {proposal, decision, deciding, error} = msg;
    const link = decision?.status === "approved" ? targetLink(decision, proposal.open) : "";
    const kind = proposal.proposalType === "qms_action" ? "Draft" : `${proposal.proposalType || "proposal"} proposal`;

    return (
        <Box sx={{alignSelf: "stretch", border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.25, backgroundColor: "#FFFFFF"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 0.5}}>
                <Chip label={kind} size="small"
                      sx={{height: 18, fontSize: 10, fontFamily: FONT, backgroundColor: "#EAF6FC", color: BLUE, "& .MuiChip-label": {px: 0.75}}} />
                {!decision && <Typography sx={{fontSize: 10.5, color: MUTED, fontFamily: FONT}}>needs your approval — nothing is written until you approve</Typography>}
            </Box>
            <Typography sx={{fontSize: 13.5, fontWeight: 700, color: TEXT, fontFamily: FONT, mb: 0.5}}>{proposal.title || "Untitled"}</Typography>
            {proposal.summary && <Typography sx={{fontSize: 12.5, color: TEXT, fontFamily: FONT, mb: 0.75, whiteSpace: "pre-wrap"}}>{proposal.summary}</Typography>}

            {decision ? (
                decision.status === "approved" ? (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                        <Typography sx={{fontSize: 12.5, color: GREEN, fontFamily: FONT, fontWeight: 600}}>✓ Done</Typography>
                        {link && (
                            <Button size="small" startIcon={<OpenInNewRounded sx={{fontSize: 15}} />} onClick={() => onOpen(link)}
                                    sx={{textTransform: "none", fontFamily: FONT, fontSize: 12, color: BLUE, minWidth: 0}}>
                                Open it
                            </Button>
                        )}
                    </Box>
                ) : (
                    <Typography sx={{fontSize: 12.5, color: MUTED, fontFamily: FONT}}>Rejected — nothing was written</Typography>
                )
            ) : deciding ? (
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <CircularProgress size={12} sx={{color: BLUE}} />
                    <Typography sx={{fontSize: 12, color: MUTED, fontFamily: FONT}}>Working…</Typography>
                </Box>
            ) : (
                <Box sx={{display: "flex", gap: 1}}>
                    <Button size="small" variant="contained" startIcon={<CheckRounded sx={{fontSize: 15}} />} onClick={() => onDecide(proposal.usid, "approve")}
                            sx={{textTransform: "none", fontFamily: FONT, fontSize: 12, boxShadow: "none", backgroundColor: BLUE, "&:hover": {backgroundColor: "#0E6FA6", boxShadow: "none"}}}>
                        Approve
                    </Button>
                    <Button size="small" startIcon={<CloseRounded sx={{fontSize: 15}} />} onClick={() => onDecide(proposal.usid, "reject")}
                            sx={{textTransform: "none", fontFamily: FONT, fontSize: 12, color: MUTED, minWidth: 0}}>
                        Reject
                    </Button>
                </Box>
            )}
            {error && <Typography sx={{fontSize: 11.5, color: "#B3261E", fontFamily: FONT, mt: 0.5}}>{error}</Typography>}
        </Box>
    );
}


/** Which model answers, chosen by the person within what their administrator allows, with the price of a
 *  typical turn beside each (2026-10-31; it reverses the 10-29 rule that nothing about the model is shown).
 *  Drawn only when there is a choice to make: one allowed model is a fact, not a control. */
function ModelPicker({open}: {open: boolean}) {
    const [models, setModels] = useState<AssistantModels | null>(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        getAssistantModels().then(got => { if (!cancelled) setModels(got); }).catch(() => { if (!cancelled) setModels(null); });
        return () => { cancelled = true; };
    }, [open]);
    const allowed = (models?.Models ?? []).filter(m => m.Allowed);
    if (!models || allowed.length < 2) return null;
    const choose = (name: string) => {
        setBusy(true);
        setAssistantModel(name === models.Default && !models.Pick ? null : name).then(setModels).catch(() => undefined).finally(() => setBusy(false));
    };
    return (
        <Tooltip title={`The model that answers. ${allowed.map(m => `${m.Label}: ${perTurnLabel(m.PerTurn) ?? "unpriced"}`).join("; ")}.`}>
            <select aria-label="assistant model" value={models.Chosen} disabled={busy} onChange={e => choose(e.target.value)}
                    style={{fontSize: 12, fontFamily: FONT, color: MUTED, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "3px 6px", background: "#fff", maxWidth: 180}}>
                {allowed.map(m => (
                    <option key={m.Name} value={m.Name}>{m.Label.replace(/^Claude /, "")}{perTurnLabel(m.PerTurn) ? ` · ${perTurnLabel(m.PerTurn)!.replace("about ", "~").replace(" a turn", "/turn")}` : ""}</option>
                ))}
            </select>
        </Tooltip>
    );
}
